import React from 'react';
import { motion } from 'framer-motion';
import { FaConnectdevelop, FaBrain, FaBell, FaArrowRight } from 'react-icons/fa';
import './HowItWorks.css';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: FaConnectdevelop,
      title: "Get in Touch",
      description: "Fill out a short background form so we understand your migraine history. We'll confirm you're a good fit and walk you through what's involved.",
      details: [
        "Short background questionnaire",
        "No clinical screening required",
        "We'll explain the protocol clearly",
        "You decide if it's right for you"
      ]
    },
    {
      number: "02",
      icon: FaBrain,
      title: "Follow the Protocol",
      description: "For 4–6 weeks, spend about 5 minutes a day logging how you feel using our app. We may also ask you to wear a wearable device during this period.",
      details: [
        "Daily symptom check-in",
        "Optional wearable data sync",
        "Simple, structured logging",
        "No disruption to your daily life"
      ]
    },
    {
      number: "03",
      icon: FaBell,
      title: "Help Us Learn",
      description: "Your data helps us understand what to measure and how to measure it. At the end, we'll share what we found with you directly.",
      details: [
        "We analyze patterns across participants",
        "You get a personal data summary",
        "Your feedback shapes the next iteration",
        "Results shared openly where possible"
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section id="how-it-works" className="how-it-works section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">What Involvement Looks Like</h2>
          <p className="section-subtitle">
            Straightforward, low-friction, and genuinely useful — for you and for us.
          </p>
        </motion.div>

        <div className="how-it-works-content">
          <motion.div
            className="steps-container"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="step-card"
                variants={stepVariants}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="step-header">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon">
                    <step.icon />
                  </div>
                </div>

                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>

                  <ul className="step-details">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="detail-item">
                        <FaArrowRight className="detail-icon" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="app-mockup"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="phone-mockup">
              <div className="phone-screen">
                <div className="app-interface">
                  <div className="app-header">
                    <div className="app-title">Hemicron</div>
                    <div className="status-indicator safe"></div>
                  </div>
                  <div className="prediction-card">
                    <div className="prediction-title">Today's Check-in</div>
                    <div className="prediction-status low">Day 12 / 28</div>
                    <div className="prediction-details">
                      <div className="detail-item">
                        <span>Sleep:</span>
                        <span className="good">7h 20m</span>
                      </div>
                      <div className="detail-item">
                        <span>Stress:</span>
                        <span className="moderate">Moderate</span>
                      </div>
                      <div className="detail-item">
                        <span>Migraine:</span>
                        <span className="safe">None today</span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-placeholder">
                    <div className="chart-bars">
                      <div className="bar" style={{ height: '60%' }}></div>
                      <div className="bar" style={{ height: '40%' }}></div>
                      <div className="bar" style={{ height: '80%' }}></div>
                      <div className="bar" style={{ height: '30%' }}></div>
                      <div className="bar" style={{ height: '70%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;
